<template>
  <div class="case-study">

    <!-- Hero -->
    <section class="hero-section">
      <div class="hero-label">UI/UX Case</div>
      <h1 class="hero-title">{{ hero.title }}</h1>
      <p class="hero-tagline">{{ hero.tagline }}</p>
      <!-- <div class="hero-role"><span class="role-badge">{{ hero.role }}</span></div> -->
      <div class="hero-cta-group">
        <button class="cta-btn cta-primary" @click="scrollTo('flow-section')">
          Design Logic
        </button>
        <button class="cta-btn cta-secondary" @click="scrollTo('final-section')">
         View Interface
        </button>
      </div>
      <div class="hero-image-wrapper">
        <img :src="hero.coverImage" :alt="hero.title" class="hero-cover" />
      </div>
    </section>

    <!-- Story -->
    <section class="section story-section">
      <div class="story-container">
        <div class="story-eyebrow">Background</div>
        <h2 class="story-headline">{{ story.headline }}</h2>
        <p class="story-body" v-for="(p, i) in story.paragraphs" :key="i" v-html="p"></p>
      </div>
    </section>

    <!-- 01 Project Overview -->
    <section id="flow-section" class="section">
      <h2 class="section-title"><span class="section-number">01</span> Project Overview</h2>
      <div class="overview-grid">
        <div v-for="(item, i) in overview" :key="i" class="overview-card">
          <div class="overview-icon">{{ item.icon }}</div>
          <div class="overview-label">{{ item.label }}</div>
          <div class="overview-value">{{ item.value }}</div>
        </div>
      </div>
    </section>

    <!-- 02 Core Features -->
    <section class="section">
      <h2 class="section-title"><span class="section-number">02</span> Core Features</h2>
      <div class="features-grid">
        <div v-for="(f, i) in features" :key="i" class="feature-card">
          <div class="feature-icon">{{ f.icon }}</div>
          <h3 class="feature-name">{{ f.name }}</h3>
          <p class="feature-desc">{{ f.desc }}</p>
        </div>
      </div>
    </section>

    <!-- 03 Problems & Goals -->
    <section class="section">
      <h2 class="section-title"><span class="section-number">03</span> Problems &amp; Goals</h2>
      <div class="problems-grid">
        <div class="problems-col">
          <h3 class="col-title pain-title"><span class="col-icon">⚠️</span> Pain Points</h3>
          <ul class="point-list">
            <li v-for="(p, i) in problems.painPoints" :key="i" class="point-item">
              <span class="point-marker pain-marker"></span><span>{{ p }}</span>
            </li>
          </ul>
        </div>
        <div class="problems-divider"></div>
        <div class="problems-col">
          <h3 class="col-title goal-title"><span class="col-icon">🎯</span> Design Goals</h3>
          <ul class="point-list">
            <li v-for="(g, i) in problems.goals" :key="i" class="point-item">
              <span class="point-marker goal-marker"></span><span>{{ g }}</span>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <!-- 04 Research -->
    <section class="section">
      <h2 class="section-title"><span class="section-number">04</span> Research &amp; Insights</h2>
      <div class="findings-grid">
        <div v-for="(f, i) in research.findings" :key="'f'+i" class="finding-card">
          <div class="finding-stat">{{ f.stat }}</div>
          <div class="finding-text">{{ f.text }}</div>
        </div>
      </div>
      <div class="quotes-area">
        <div v-for="(q, i) in research.quotes" :key="'q'+i" class="quote-card">
          <div class="quote-mark">"</div>
          <p class="quote-text">{{ q.text }}</p>
          <div class="quote-author">— {{ q.author }}</div>
        </div>
      </div>
    </section>

    <!-- 05 Architecture & Flow -->
    <section class="section">
      <h2 class="section-title"><span class="section-number">05</span> Basic Framework</h2>
      <!-- Category Menu -->
      <div class="ia-menu">
        <button v-for="(cat, i) in detailedIA" :key="'menu-'+i" class="ia-menu-item" @click="scrollTo('cat-' + i)">
          <span class="ia-menu-icon">{{ cat.icon }}</span>
          <span class="ia-menu-name">{{ cat.name }}</span>
        </button>
      </div>

      <div class="ia-sections-wrapper">
        <div v-for="(cat, i) in detailedIA" :key="i" class="ia-module-block" :id="'cat-' + i">
          <div class="ia-module-header">
            <h3 class="ia-module-title">
              <span class="module-number">0{{ i + 1 }}</span>
              <span class="module-name">{{ cat.name }}</span>
            </h3>
          </div>
          <div class="ia-module-content">
            <div class="ia-table-header">
              <div class="ia-th">Feature</div>
              <div class="ia-th hide-mobile">Function Description</div>
              <div class="ia-th hide-mobile">User Value</div>
            </div>
            <div class="ia-table-body">
              <div v-for="(feat, j) in cat.features" :key="j" class="ia-feature-row">
                <div class="feat-cell feat-main">
                  <div class="feat-l2">{{ feat.l2 }}</div>
                  <div class="feat-l3">{{ feat.l3 }}</div>
                </div>
                <div class="feat-cell feat-desc hide-mobile">{{ feat.desc }}</div>
                <div class="feat-cell feat-value hide-mobile">
                  <span class="value-badge">{{ feat.value }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 06 Visual Showcase -->
    <section id="final-section" class="section">
      <h2 class="section-title"><span class="section-number">06</span> Visual Showcase</h2>
      <p class="section-desc">Experience Comma Talk through an interactive video demonstration and a high-fidelity UI gallery focused on our core Community and Web3 Wallet features.</p>
      
      <div class="video-showcase" style="margin-bottom: 3rem;">
        <h3 class="showcase-title">Interactive Demo</h3>
        <div class="video-placeholder">
          <!-- Video Preview Cover -->
          <div v-if="!videoPlaying" class="video-preview" @click="videoPlaying = true">
            <img src="https://img.youtube.com/vi/hqbGUNdoefo/maxresdefault.jpg" alt="Video Thumbnail" class="preview-img" />
            <div class="play-button">
              <div class="play-icon"></div>
            </div>
          </div>
          
          <!-- Actual Video Iframe -->
          <iframe 
            v-else
            class="demo-video"
            src="https://www.youtube.com/embed/hqbGUNdoefo?autoplay=1" 
            title="Comma Talk Introduction Video" 
            frameborder="0" 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
            allowfullscreen>
          </iframe>
        </div>
      </div>

      <div class="gallery-showcase">
        <h3 class="showcase-title">Final Interface Showcase</h3>
        <div class="ui-gallery">
          <div v-for="(img, i) in showcaseImages" :key="i" class="gallery-item">
            <img :src="img" alt="UI Screen Showcase" loading="lazy" />
          </div>
        </div>
      </div>
    </section>

    <!-- 07 Results -->
    <section class="section">
      <h2 class="section-title"><span class="section-number">07</span> Results &amp; Takeaways</h2>

      <div class="takeaways-list-vertical">
        <!-- 1. Key Achievements -->
        <div class="takeaway-section">
          <div class="section-label">
            <span class="label-icon">🏆</span>
            <h3 class="label-text">Key Achievements</h3>
          </div>
          <ul class="points-list">
            <li v-for="(a, i) in results.achievements" :key="'a'+i">{{ a }}</li>
          </ul>
        </div>

        <!-- 2. Core Learnings -->
        <div class="takeaway-section">
          <div class="section-label">
            <span class="label-icon">💡</span>
            <h3 class="label-text">Core Learnings</h3>
          </div>
          <ul class="points-list">
            <li v-for="(l, i) in results.learnings" :key="'l'+i">{{ l }}</li>
          </ul>
        </div>

        <!-- 3. Extension Thinking -->
        <div class="takeaway-section">
          <div class="section-label">
            <span class="label-icon">🧠</span>
            <h3 class="label-text">Extension Thinking</h3>
          </div>
          <ul class="points-list">
            <li v-for="(e, i) in results.reflections" :key="'e'+i">{{ e }}</li>
          </ul>
        </div>
      </div>
    </section>

  </div>
  <HomeButton />
</template>

<script>
import HomeButton from '@/components/common/HomeButton.vue'
export default {
  name: 'UIDesign',
  components: { HomeButton },
  methods: {
    scrollTo(id) {
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  },
  data() {
    return {
      videoPlaying: false,
      hero: {
        title: 'Comma Talk',
        tagline: 'A multilingual chat app with real-time translation, vibrant communities, and an integrated Web3 wallet — connecting the world without language barriers.',
        role: 'UI/UX Design',
        coverImage: require('@/assets/img/ui/comma_talk_hero.png')
      },
      story: {
        headline: 'What is Comma Talk?',
        paragraphs: [
          '<strong>Overview:</strong> In a global landscape spanning over 88 languages, most communication platforms remain limited by single-language interfaces. This creates significant barriers for travelers navigating local interactions, international teams striving to preserve nuance in translation, and crypto communities fragmented by language divides. Comma Talk addresses these challenges by enabling seamless, real-time multilingual communication.',
          '<strong>As a UI/UX Designer at Comma Global, I led interface optimization and visual redesign efforts across the product. My work included refining the visual system, optimizing cross-platform UI consistency, delivering high-fidelity screen designs, enhancing interaction details, and building comprehensive design system documentation to ensure smooth development handoff.',
        ]
      },
      overview: [
        { icon: '👤', label: 'Role', value: 'UI/UX Designer' },
        { icon: '📅', label: 'Duration', value: '1 year' },
        { icon: '📱', label: 'Platform', value: 'iOS / Android' },
        { icon: '👥', label: 'Team', value: '3 Designer, 3 Developers' }
      ],
      features: [
        { icon: '🌐', name: 'Real-Time Translation', desc: 'Supports 88 languages with instant in-chat translation. Messages display both original and translated text inline.' },
        { icon: '🎙️', name: 'Voice Translation', desc: 'Speak in your language and send translated voice messages. AI-powered speech recognition for accurate, fast translations.' },
        { icon: '🏘️', name: 'Global Communities', desc: 'Discover and join interest-based communities across languages. Language tags let you filter content in your preferred language.' },
        { icon: '💎', name: 'Web3 Wallet', desc: 'Built-in crypto wallet supporting multi-chain assets. Send, receive, and swap tokens directly within conversations.' },
        { icon: '📍', name: 'GPS Safety Check', desc: 'Share real-time location with trusted contacts. Safety confirmation system works anywhere on Earth.' },
        { icon: '💳', name: 'Comma Pay', desc: 'Integrated payment system for seamless cross-border transactions. Pay anyone, anywhere, in any currency.' }
      ],
      problems: {
        painPoints: [
          'Existing chat apps require switching to external tools for translation, breaking conversation flow',
          'Crypto wallet apps are intimidating for non-technical users, especially across language barriers',
          'Global communities are fragmented — users cannot discover content outside their language bubble'
        ],
        goals: [
          'Build seamless in-chat translation that feels invisible and natural to use',
          'Design a Web3 wallet simple enough for first-time crypto users',
          'Create a community discovery system that transcends language boundaries'
        ]
      },
      research: {
        findings: [
          { stat: '73%', text: 'of multilingual users say switching between chat and translation apps is frustrating' },
          { stat: '88', text: 'languages supported by Comma Talk\'s real-time translation engine' },
          { stat: '61%', text: 'of potential crypto users abandon wallet setup due to complex, English-only interfaces' }
        ],
        quotes: [
          { text: 'I chat with friends in 4 different languages. Having translation built right into the chat would be a game changer.', author: 'User Interview #7, International Student' }
        ]
      },
      detailedIA: [
        {
          name: 'Friend', icon: '👥',
          features: [
            { l2: 'Friend List', l3: 'All / Online', desc: 'Friend list showing real-time online status', value: 'Quickly establish contacts' },
            { l2: 'Add Friend', l3: 'Search / QR / Invite', desc: 'Multiple ways to add new friends', value: 'Expand social circle' },
            { l2: 'Friend Requests', l3: 'Incoming / Sent', desc: 'Management of incoming and outgoing requests', value: 'Control social relationships' },
            { l2: 'Friend Profile', l3: 'Info / Languages', desc: 'Profile details with language indicators', value: 'Pre-understand communication style' },
            { l2: 'Favorites', l3: 'Starred Contacts', desc: 'Quick access to frequently contacted people', value: 'Increase efficiency' },
            { l2: 'Block List', l3: 'Restricted Users', desc: 'Manage restricted or blocked users', value: 'Security control' }
          ]
        },
        {
          name: 'Chat', icon: '💬',
          features: [
            { l2: 'Chat List', l3: 'All Chats', desc: 'Entry point for all conversations', value: 'Centralized management' },
            { l2: '1:1 Chat', l3: 'Text / Emoji', desc: 'Direct messaging capability', value: 'Basic communication' },
            { l2: 'Group Chat', l3: 'Multi-user', desc: 'Real-time multi-user interaction', value: 'Team collaboration' },
            { l2: 'Voice Messages', l3: 'Record / Playback', desc: 'Voice-based communication', value: 'Enhance expression' },
            { l2: 'Media & Files', l3: 'Image / Video / Docs', desc: 'Support for various file formats', value: 'Information delivery' },
            { l2: 'In-chat Actions', l3: 'Send / Tip / Share', desc: 'Rich in-chat interactions including payments', value: 'Scenario integration' },
            { l2: 'Chat Settings', l3: 'Pin / Mute / Delete', desc: 'Customizable chat management', value: 'Personalized experience' }
          ]
        },
        {
          name: 'Translation', icon: '🌐',
          features: [
            { l2: 'Real-time Chat Translation', l3: 'Auto / Toggle', desc: 'Instant message translation during chat', value: 'Barrier-free communication' },
            { l2: 'Voice Translation', l3: 'Live Speech', desc: 'Real-time live voice translation', value: 'Cross-language dialogue' },
            { l2: 'Message Translate', l3: 'Tap to Translate', desc: 'Precise translation for individual messages', value: 'Precise understanding' },
            { l2: 'Language Preferences', l3: 'Input / Output', desc: 'Comprehensive language preference settings', value: 'Personalized experience' },
            { l2: 'Translation History', l3: 'Saved Records', desc: 'Repository of previous translations', value: 'Information reuse' },
            { l2: 'AI Context Optimization', l3: 'Tone / Context', desc: 'AI-driven context and tone adjustment', value: 'Preserve semantics' }
          ]
        },
        {
          name: 'CM Channel', icon: '🌍',
          features: [
            { l2: 'Discover', l3: 'Recommended', desc: 'Discover new and relevant communities', value: 'Content discovery' },
            { l2: 'Channels', l3: 'Topic-based', desc: 'Browse structured community channels', value: 'Clear navigation' },
            { l2: 'My Channels', l3: 'Joined / Created', desc: 'Manage your personal channel subscriptions', value: 'Sense of belonging' },
            { l2: 'Channel Chat', l3: 'Posts / Threads', desc: 'In-depth community discussions and topics', value: 'Deep interaction' },
            { l2: 'Events', l3: 'Online / Offline', desc: 'Engagement in community activities', value: 'Boost activity' },
            { l2: 'Trending', l3: 'Hot Topics', desc: 'Stay updated with global hot topics', value: 'Stay informed' },
            { l2: 'Create Channel', l3: 'Setup Flow', desc: 'Simplified process to build your own community', value: 'User growth' }
          ]
        },
        {
          name: 'Web3 Wallet', icon: '💎',
          features: [
            { l2: 'Wallet Overview', l3: 'Crypto & Assets', desc: 'Unified view of Web3 tokens and fiat balance', value: 'Full asset control' },
            { l2: 'Send / Receive', l3: 'Quick Crypto Transfer', desc: 'Blockchain-based transfers and simple QR payments', value: 'Fast cross-border service' },
            { l2: 'Transaction History', l3: 'All / Chat-linked', desc: 'Records linked directly back to chat source', value: 'Contextual association' },
            { l2: 'Swap', l3: 'Token Exchange', desc: 'Seamless decentralized crypto swap within the app', value: 'High asset liquidity' },
            { l2: 'Comma Pay', l3: 'Payment Flow', desc: 'Native application payment processing', value: 'Business closed loop' },
            { l2: 'Profile', l3: 'Avatar / Bio', desc: 'Public identity and personal profile', value: 'Identity display' },
            { l2: 'Language Settings', l3: 'Preferences', desc: 'Manage core language and app settings', value: 'Core experience' },
            { l2: 'Security', l3: 'Verification / Privacy', desc: 'Advanced fund and privacy management', value: 'Trust guarantee' },
            { l2: 'Settings', l3: 'Notification / General', desc: 'General app behavior customization', value: 'Personalization' }
          ]
        }
      ],
      showcaseImages: [
        require('@/assets/img/ui/1.png'),
        require('@/assets/img/ui/2.png'),
        require('@/assets/img/ui/4.png'),
        require('@/assets/img/ui/5.jpg'),
        require('@/assets/img/ui/3.png'),
        require('@/assets/img/ui/6.jpg')
                
      ],
      results: {
        achievements: [
          'Designed a multilingual communication system with real-time translation embedded directly into chat and voice interactions, reducing friction in cross-language conversations.',
          'Integrated wallet functionality into chat scenarios (e.g., in-conversation transfers), enabling users to complete social and financial actions within a single flow.',
          'Structured a topic-based community system to support content discovery, participation, and scalable user growth.'
        ],
        learnings: [
          'Context matters more than accuracy: Users rely on original messages alongside translations to verify intent, especially in sensitive or nuanced conversations.',
          'Web3 features require simplification: Progressive disclosure (e.g., hiding advanced wallet functions by default) helps reduce confusion for non-crypto users.',
          'Interaction efficiency is critical: One-tap actions (translate, reply, send) significantly improve usability in multilingual environments where cognitive load is higher.'
        ],
        reflections: [
          'Beyond literal translation: Exploring how AI can assist in tone adjustment (e.g., formal vs casual) to improve communication clarity across cultures.',
          'Community governance: Potential to introduce lightweight moderation or voting mechanisms within channels to support self-managed communities.',
          'Unified identity system: Combining user profile, language preferences, and wallet into a single identity layer to reduce fragmentation across features.'
        ]
      }
    }
  }
}
</script>

<style scoped>
/* ===== Global ===== */
.case-study { max-width: 1100px; margin: 0 auto; padding: 2rem 1.5rem 4rem; color: #fff; }
.section { margin-bottom: 5rem; }
.section-title { font-size: 2rem; font-weight: 700; color: #fff; margin-bottom: 2.5rem; display: flex; align-items: center; gap: 1rem; }
.section-number { font-size: 1rem; font-weight: 700; color: #73FFF6; background: rgba(115,255,246,.1); border: 1px solid rgba(115,255,246,.25); padding: .3rem .7rem; border-radius: 6px; letter-spacing: 1px; }

/* ===== Hero ===== */
.hero-section { text-align: center; padding: 3rem 0 4rem; }
.hero-label { display: inline-block; font-size: .85rem; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; color: #73FFF6; background: rgba(115,255,246,.1); padding: .4rem 1.2rem; border-radius: 20px; border: 1px solid rgba(115,255,246,.2); margin-bottom: 1.5rem; }
.hero-title { font-size: 3.2rem; font-weight: 800; line-height: 1.15; margin-bottom: 1.2rem; background: linear-gradient(135deg, #fff, #73FFF6); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
.hero-tagline { font-size: 1.2rem; color: rgba(255,255,255,.7); max-width: 720px; margin: 0 auto 1.5rem; line-height: 1.7; }
.hero-role { margin-bottom: 2rem; }
.role-badge { display: inline-block; font-size: .9rem; font-weight: 600; color: #fff; background: rgba(255,255,255,.08); border: 1px solid rgba(255,255,255,.15); padding: .45rem 1.4rem; border-radius: 24px; }
.hero-cta-group { display: flex; gap: 1rem; justify-content: center; margin-bottom: 3rem; flex-wrap: wrap; }
.cta-btn { display: inline-flex; align-items: center; gap: .5rem; font-family: inherit; font-size: 1.05rem; font-weight: 600; padding: .85rem 2rem; border-radius: 12px; cursor: pointer; transition: all .3s; border: none; }
.cta-icon { font-size: 1.15rem; }
.cta-primary { background: linear-gradient(135deg, #73FFF6, #5B8DEF); color: #0a0a0a; box-shadow: 0 4px 20px rgba(115,255,246,.3); }
.cta-primary:hover { transform: translateY(-2px); box-shadow: 0 8px 30px rgba(115,255,246,.45); }
.cta-secondary { background: transparent; color: #73FFF6; border: 2px solid rgba(115,255,246,.4); }
.cta-secondary:hover { background: rgba(115,255,246,.1); transform: translateY(-2px); border-color: rgba(115,255,246,.7); }
.hero-image-wrapper { border-radius: 16px; overflow: hidden; box-shadow: 0 20px 60px rgba(0,0,0,.4), 0 0 40px rgba(115,255,246,.08); border: 1px solid rgba(255,255,255,.08); }
.hero-cover { width: 100%; height: auto; display: block; }

/* ===== Story ===== */
.story-container { background: linear-gradient(135deg, rgba(115,255,246,.04), rgba(91,141,239,.04)); border: 1px solid rgba(115,255,246,.12); border-radius: 20px; padding: 3rem; position: relative; overflow: hidden; }
.story-container::before { content: ''; position: absolute; top: 0; left: 0; width: 4px; height: 100%; background: linear-gradient(to bottom, #73FFF6, #5B8DEF); border-radius: 4px 0 0 4px; }
.story-eyebrow { font-size: .8rem; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: #73FFF6; margin-bottom: 1rem; }
.story-headline { font-size: 1.8rem; font-weight: 700; color: #fff; margin-bottom: 1.5rem; line-height: 1.3; }
.story-body { font-size: 1.1rem; color: rgba(255,255,255,.75); line-height: 1.85; margin-bottom: 1rem; }
.story-body:last-child { margin-bottom: 0; }

/* ===== Overview ===== */
.overview-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 1.5rem; }
.overview-card { background: rgba(255,255,255,.04); border: 1px solid rgba(255,255,255,.08); border-radius: 14px; padding: 2rem 1.5rem; text-align: center; transition: transform .3s, border-color .3s; }
.overview-card:hover { transform: translateY(-4px); border-color: rgba(115,255,246,.3); }
.overview-icon { font-size: 2rem; margin-bottom: .8rem; }
.overview-label { font-size: .8rem; text-transform: uppercase; letter-spacing: 1.5px; color: rgba(255,255,255,.45); margin-bottom: .5rem; }
.overview-value { font-size: 1.1rem; font-weight: 600; color: #fff; }

/* ===== Features ===== */
.features-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 1.5rem; }
.feature-card { background: rgba(255,255,255,.04); border: 1px solid rgba(255,255,255,.08); border-radius: 16px; padding: 2rem 1.5rem; transition: transform .3s, border-color .3s; }
.feature-card:hover { transform: translateY(-4px); border-color: rgba(115,255,246,.3); }
.feature-icon { font-size: 2.2rem; margin-bottom: .8rem; }
.feature-name { font-size: 1.15rem; font-weight: 700; color: #73FFF6; margin-bottom: .6rem; }
.feature-desc { font-size: 1rem; color: rgba(255,255,255,.65); line-height: 1.6; }

/* ===== Problems ===== */
.problems-grid { display: grid; grid-template-columns: 1fr auto 1fr; gap: 2rem; align-items: start; }
.problems-divider { width: 1px; background: linear-gradient(to bottom, transparent, rgba(255,255,255,.15), transparent); align-self: stretch; }
.col-title { font-size: 1.3rem; font-weight: 600; margin-bottom: 1.5rem; display: flex; align-items: center; gap: .6rem; }
.col-icon { font-size: 1.4rem; }
.pain-title { color: #FF6B6B; }
.goal-title { color: #73FFF6; }
.point-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 1.2rem; }
.point-item { display: flex; align-items: flex-start; gap: .8rem; font-size: 1.05rem; color: rgba(255,255,255,.8); line-height: 1.6; }
.point-marker { flex-shrink: 0; width: 8px; height: 8px; border-radius: 50%; margin-top: 8px; }
.pain-marker { background: #FF6B6B; box-shadow: 0 0 8px rgba(255,107,107,.4); }
.goal-marker { background: #73FFF6; box-shadow: 0 0 8px rgba(115,255,246,.4); }

/* ===== Research ===== */
.findings-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 1.5rem; margin-bottom: 2.5rem; }
.finding-card { background: rgba(255,255,255,.04); border: 1px solid rgba(255,255,255,.08); border-radius: 14px; padding: 2.5rem 1.5rem; text-align: center; transition: transform .3s, border-color .3s; }
.finding-card:hover { transform: translateY(-4px); border-color: rgba(115,255,246,.3); }
.finding-stat { font-size: 3rem; font-weight: 800; color: #73FFF6; margin-bottom: .8rem; }
.finding-text { font-size: 1.05rem; color: rgba(255,255,255,.65); line-height: 1.6; }
.quotes-area { display: flex; flex-direction: column; gap: 1.5rem; }
.quote-card { background: rgba(255,255,255,.03); border-left: 3px solid #73FFF6; border-radius: 0 12px 12px 0; padding: 2rem 2.5rem; position: relative; }
.quote-mark { position: absolute; top: 8px; left: 16px; font-size: 3rem; color: rgba(115,255,246,.15); font-family: Georgia, serif; line-height: 1; }
.quote-text { font-size: 1.15rem; color: rgba(255,255,255,.8); font-style: italic; line-height: 1.7; margin-bottom: .6rem; padding-left: 1rem; }
.quote-author { font-size: .9rem; color: rgba(255,255,255,.45); padding-left: 1rem; }

/* ===== Information Architecture (Categorized Table) ===== */
.ia-menu { display: flex; flex-wrap: wrap; gap: 1rem; margin-bottom: 2.5rem; }
.ia-menu-item { display: inline-flex; align-items: center; gap: 0.5rem; font-family: inherit; background: rgba(255,255,255,.05); border: 1px solid rgba(255,255,255,.1); padding: 0.6rem 1.2rem; border-radius: 30px; color: #fff; cursor: pointer; transition: all 0.3s ease; }
.ia-menu-item:hover { background: rgba(115,255,246,.15); border-color: rgba(115,255,246,.4); transform: translateY(-2px); }
.ia-menu-icon { font-size: 1.2rem; }
.ia-menu-name { font-size: 0.95rem; font-weight: 600; }

.ia-sections-wrapper { display: flex; flex-direction: column; gap: 3.5rem; }
.ia-module-block { background: rgba(255,255,255,.02); border: 1px solid rgba(255,255,255,.05); border-radius: 20px; overflow: hidden; }
.ia-module-header { padding: 1.5rem 2rem; background: rgba(115,255,246,.08); border-bottom: 1px solid rgba(115,255,246,.15); }
.ia-module-title { display: flex; align-items: center; gap: .8rem; margin: 0; }
.module-number { font-size: .8rem; color: #73FFF6; opacity: .6; font-family: monospace; }
.module-name { font-size: 1.2rem; font-weight: 700; color: #fff; }

.ia-table-header { display: grid; grid-template-columns: 240px 1fr 200px; padding: .8rem 2rem; background: rgba(255,255,255,.03); border-bottom: 1px solid rgba(255,255,255,.05); }
.ia-th { font-size: .75rem; font-weight: 700; color: rgba(255,255,255,.4); text-transform: uppercase; letter-spacing: 1px; }

.ia-feature-row { display: grid; grid-template-columns: 240px 1fr 200px; padding: 1.2rem 2rem; border-bottom: 1px solid rgba(255,255,255,.03); transition: background .3s; }
.ia-feature-row:hover { background: rgba(115,255,246,.03); }
.ia-feature-row:last-child { border-bottom: none; }

.feat-cell { display: flex; align-items: center; }
.feat-main { flex-direction: column; align-items: flex-start; gap: .2rem; }
.feat-l2 { font-size: 1rem; font-weight: 600; color: #fff; }
.feat-l3 { font-size: .8rem; color: #73FFF6; opacity: .7; }
.feat-desc { font-size: .95rem; color: rgba(255,255,255,.6); line-height: 1.6; padding-right: 2rem; }
.value-badge { font-size: .8rem; color: #73FFF6; background: rgba(115,255,246,.08); border: 1px solid rgba(115,255,246,.2); padding: .35rem .8rem; border-radius: 6px; }

.arch-subtitle { font-size: 1.3rem; color: #73FFF6; margin-bottom: 1.5rem; font-weight: 600; }
.flow-image-wrapper { border-radius: 16px; overflow: hidden; border: 1px solid rgba(255,255,255,.08); }
.flow-image-wrapper img { width: 100%; height: auto; display: block; }

/* ===== Iterations ===== */
.iteration-block { margin-bottom: 3.5rem; }
.iteration-block:last-child { margin-bottom: 0; }
.iteration-header { margin-bottom: 2rem; }
.iteration-badge { display: inline-block; font-size: .75rem; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: #73FFF6; background: rgba(115,255,246,.1); padding: .3rem .8rem; border-radius: 4px; margin-bottom: .8rem; }
.iteration-name { font-size: 1.5rem; font-weight: 700; color: #fff; margin-bottom: .5rem; }
.iteration-desc { font-size: 1.05rem; color: rgba(255,255,255,.6); line-height: 1.7; }
.iteration-compare { display: grid; grid-template-columns: 1fr auto 1fr; gap: 1.5rem; align-items: center; }
.compare-item { background: rgba(255,255,255,.03); border: 1px solid rgba(255,255,255,.08); border-radius: 14px; overflow: hidden; }
.compare-label { text-align: center; font-size: .8rem; font-weight: 600; text-transform: uppercase; letter-spacing: 1.5px; color: rgba(255,255,255,.45); padding: .8rem; background: rgba(255,255,255,.03); border-bottom: 1px solid rgba(255,255,255,.06); }
.compare-image img { width: 100%; height: auto; display: block; transition: transform .5s; }
.compare-item:hover .compare-image img { transform: scale(1.03); }
.compare-arrow { display: flex; align-items: center; justify-content: center; }

/* ===== Final Solution / Showcase ===== */
.section-desc { font-size: 1.15rem; color: rgba(255,255,255,.75); line-height: 1.7; margin-bottom: 2.5rem; max-width: 800px; }
.video-showcase { margin-bottom: 4rem; background: rgba(255,255,255,.02); padding: 2.5rem; border-radius: 20px; border: 1px solid rgba(255,255,255,.05); }
.video-placeholder { position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; border-radius: 12px; background: #111; box-shadow: 0 20px 40px rgba(0,0,0,.5); border: 1px solid rgba(255,255,255,.08); }
.demo-video { position: absolute; top: 0; left: 0; width: 100%; height: 100%; }

.video-preview { position: absolute; top: 0; left: 0; width: 100%; height: 100%; cursor: pointer; display: flex; align-items: center; justify-content: center; overflow: hidden; }
.preview-img { width: 100%; height: 100%; object-fit: cover; transition: transform .5s; }
.video-preview:hover .preview-img { transform: scale(1.05); }

.play-button { position: absolute; width: 80px; height: 80px; background: rgba(115,255,246,.9); border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 0 30px rgba(115,255,246,.4); transition: transform .3s, background .3s; z-index: 2; }
.play-icon { width: 0; height: 0; border-style: solid; border-width: 15px 0 15px 25px; border-color: transparent transparent transparent #111; margin-left: 5px; }
.video-preview:hover .play-button { transform: scale(1.1); background: #3c9cff; }
.showcase-title { color: #73FFF6; font-size: 1.6rem; margin-bottom: .5rem; font-weight: 700; }
.showcase-desc { color: rgba(255,255,255,.5); margin-bottom: 1.5rem; font-size: 1.05rem; }
.ui-gallery-title { margin-bottom: 2rem; }
.ui-gallery { column-count: 3; column-gap: 2rem; orphans: 1; widows: 1; }
.gallery-item { break-inside: avoid; display: block; margin-bottom: 2rem; background: rgba(255,255,255,.02); padding: 1rem; border-radius: 20px; border: 1px solid rgba(255,255,255,.08); box-shadow: 0 10px 30px rgba(0,0,0,.3); transition: transform .4s cubic-bezier(0.175, 0.885, 0.32, 1.275), box-shadow .4s; }
.gallery-item:hover { transform: translateY(-8px); box-shadow: 0 25px 60px rgba(115,255,246,.15); border-color: rgba(115,255,246,.4); }
.gallery-item img { width: 100%; height: auto; display: block; border-radius: 12px; transition: transform .5s; }
.gallery-item:hover img { transform: scale(1.03); }

/* ===== Results ===== */
.metrics-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 1.5rem; margin-bottom: 3rem; }
.metric-card { background: rgba(115,255,246,.05); border: 1px solid rgba(115,255,246,.15); border-radius: 14px; padding: 2.5rem 1.5rem; text-align: center; transition: transform .3s; }
.metric-card:hover { transform: translateY(-4px); }
.metric-value { font-size: 2.5rem; font-weight: 800; color: #73FFF6; margin-bottom: .6rem; }
.metric-label { font-size: 1rem; color: rgba(255,255,255,.6); line-height: 1.5; }
.takeaways-list-vertical { display: flex; flex-direction: column; gap: 2rem; }
.takeaway-section { background: rgba(255,255,255,.02); border: 1px solid rgba(255,255,255,.05); border-radius: 16px; padding: 2rem; display: grid; grid-template-columns: 240px 1fr; gap: 2rem; align-items: start; transition: border-color .3s; }
.takeaway-section:hover { border-color: rgba(115,255,246,.2); }
.section-label { display: flex; align-items: center; gap: .8rem; }
.label-icon { font-size: 1.5rem; }
.label-text { font-size: 1.1rem; font-weight: 700; color: #73FFF6; margin: 0; text-transform: uppercase; letter-spacing: 1px; }

.points-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 1rem; }
.points-list li { font-size: 1.05rem; color: rgba(255,255,255,.75); line-height: 1.7; padding-left: 1.5rem; position: relative; }
.points-list li::before { content: '→'; position: absolute; left: 0; color: #73FFF6; font-weight: 700; }

/* ===== Responsive ===== */
@media (max-width: 992px) {
  .hero-title { font-size: 2.2rem; }
  .overview-grid { grid-template-columns: repeat(2,1fr); }
  .features-grid { grid-template-columns: 1fr; }
  .findings-grid { grid-template-columns: 1fr; }
  .metrics-grid { grid-template-columns: 1fr; }
  .problems-grid { grid-template-columns: 1fr; }
  .problems-divider { width: 100%; height: 1px; }
  .iteration-compare { grid-template-columns: 1fr; gap: 1rem; }
  .compare-arrow { transform: rotate(90deg); }
  .takeaways-list-vertical { gap: 1.5rem; }
  .takeaway-section { grid-template-columns: 1fr; gap: 1.2rem; padding: 1.5rem; }
  .story-container { padding: 2rem 1.5rem; }
  .ui-gallery { column-count: 2; }
}
@media (max-width: 576px) {
  .case-study { padding: 1rem 1rem 3rem; }
  .hero-title { font-size: 1.8rem; }
  .section-title { font-size: 1.5rem; }
  .overview-grid { grid-template-columns: 1fr; }
  .hero-cta-group { flex-direction: column; align-items: center; }
  .cta-btn { width: 100%; justify-content: center; }
  .story-container { padding: 1.5rem 1.2rem; }
  .story-headline { font-size: 1.3rem; }
  .tree-branches { flex-direction: column; align-items: center; }
  .video-showcase { padding: 1.5rem 1rem; }
  .ui-gallery { column-count: 1; gap: 1rem; }
}
</style>
